import { Box, Card, Divider, Grid, Tab, Tabs, Typography } from '@mui/material';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { EpisodeOfCareWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/EpisodeOfCare';
import {
  useConditions,
  useEncounters,
  useEpisodeOfCares,
  useObservations,
  useServiceRequests,
  useTasks,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import { TabPanel } from 'src/careflow/ph-core/fhir-organization/pages/FhirOrganizationDetails';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { WorkflowHeader } from 'src/careflow/ph-core/workflow/common/WorkflowHeader';
import LoadingScreen from 'src/components/LoadingScreen';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { mapEpisodeOfCareResources } from '../../patient/episodeOfCare/EpisodeOfCareList';
import { CalendarToday } from '@mui/icons-material';
import PageContainer from 'src/careflow/common/PageContainer';
import General from '../components/General';
import StatusHistory from '../components/StatusHistory';
import { useSnackbar } from 'notistack';
import Workflow, { WorkflowData } from 'src/careflow/ph-core/workflow/Workflow';
import useObjectState from 'src/hooks/useObjectState';
import { assignOwnerHandler } from 'src/careflow/ph-core/workflow/handlers';
import {
  checkAclValidation,
  getRelatedAcls,
} from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import { UserService } from 'src/careflow/ph-core/admin/user/UserService';
import { isEmpty, isObject, sortBy } from 'lodash';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { NonExistingInformation } from 'src/careflow/ph-core/workflow/components/common/ui/NonExistingInformation';
import {
  buildMappingChecklistItem,
  handlerGenericStepWorkflow,
  workflowInstanceData,
} from 'src/careflow/ph-core/workflow/helpers/workflow';
import { ObservationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import { useDialogStore } from 'src/stores/dialog';
import { WorkflowService } from 'src/careflow/ph-core/workflow/api/WorkflowService';
import { EpisodeOfCareService } from '../service/EpisodeOfCareService';
import { registerSteps } from 'src/careflow/ph-core/workflow/registry/workflowRegistry';
import ObservationDetails from '../../patient/observations/ObservationDetails';
import { AssessmentChecklistDataButton } from 'src/careflow/engagement-hub/patient-engagement/patient/components/workflow-step/AssessmentFromChecklistData';
import { TaskWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import useUserPractitionerRole from 'src/hooks/useUserPractitionerRole';
import TasksByPathway from '../../task/patient/TasksByPathway';
import { useTaskActivityDefinition, useTaskAssessment } from 'src/careflow/helpers/hooks';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { commonMenuTaskHandlers } from '../../task/common/handlers';
import { TaskPermissions } from 'src/careflow/helpers/types';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import PatientGenericServiceRequest, {
  ServiceRequestsPermissions,
} from '../../patient/service-request/PatientGenericServiceRequest';
import { ConditionWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import { CREATE_TASK, UPDATE_TASK, useEventBus } from 'src/event-bus';
import PatientCase from '../../patient/cases/patientCase';
import { ServiceRequestWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { CaseService } from 'src/crs/case/service/CaseService';
import { PaginateQuery } from 'src/api/pagination/dtos';
import { WorkflowStatistics } from 'src/careflow/ph-core/workflow/common/WorkflowStatistics';
import Diagnosis from '../components/Diagnosis';
import Documents from '../components/Documents';
import Cases from '../components/Cases';

export default function EpisodeOfCareDetails() {
  const { uuid: episodeOfCareId = null } = useParams();
  const { i18n } = useLocales();
  const { configurations, componentsData } = useTenantConfigData();
  const { enqueueSnackbar } = useSnackbar();
  const { confirmDialog, closeDialog } = useDialogStore();
  const { onEvent, removeEvent } = useEventBus();
  const authUser = useAuth().getCurrentUser();
  const userEmail = authUser?.email;

  const [eoc, { isLoading, update, refresh }] = useEpisodeOfCares({
    filter: {
      _id: episodeOfCareId,
      _include: [
        'EpisodeOfCare:patient',
        'EpisodeOfCare:organization',
        'EpisodeOfCare:incoming-referral',
        'EpisodeOfCare:care-manager',
        'EpisodeOfCare:condition',
      ],
      '_include:iterate': ['PractitionerRole:practitioner'],
    },
    map: EpisodeOfCareWrapper,
    autofetch: Boolean(episodeOfCareId),
  });

  const eocMapped = useMemo(() => mapEpisodeOfCareResources(eoc)?.[0], [eoc]);

  const { includedResources, ...resource } = eocMapped ?? {};
  const { subject } = includedResources ?? {};

  const [encounters] = useEncounters({
    filter: {
      subject: subject?.id,
      'episode-of-care': resource?.id,
    },
    autofetch: !!subject?.id && !!resource?.id,
  });

  const [observations, { update: updateObservation }] = useObservations({
    filter: {
      patient: subject?.id,
    },
    map: ObservationWrapper,
    autofetch: !!subject?.id,
  });

  const [conditions] = useConditions({
    filter: {
      patient: subject?.id,
    },
    map: ConditionWrapper,
    autofetch: !!subject?.id,
  });

  const [tasks, { refresh: refreshTasks }] = useTasks({
    filter: { subject: subject?.id },
    map: TaskWrapper,
    autofetch: !!subject?.id,
  });

  const [serviceRequests] = useServiceRequests({
    filter: { subject: subject?.id },
    map: ServiceRequestWrapper,
    autofetch: !!subject?.id,
  });

  const getCaseList = async () => {
    const paginationQuery: PaginateQuery = {};
    paginationQuery.filter = {
      indexedSubjectFhirRefUri: `${configurations.fhir.baseUrl}/Patient/${subject?.id}`,
    };
    const cases = await CaseService.getAll(paginationQuery);
    if (cases) {
      setCaseData(cases);
    }
  };

  useEffect(() => {
    if (configurations && subject) {
      getCaseList();
    }
  }, [subject, configurations]);

  const { practitionerRoles } = useUserPractitionerRole();
  const taskActivityDefinition = useTaskActivityDefinition();
  const { handleAssessmentSelect } = useTaskAssessment(subject ?? null, tasks, refreshTasks);

  const [currentTab, setCurrentTab] = useState(0);
  const [allUsers, setAllUsers] = useState<any>([]);
  const [caseData, setCaseData] = useState<any>([]);
  const [
    { initialWorkflow, workflowInstance, workflowStage, checklistData, eocRecord, statisticsData },
    updateWorkflowDetail,
  ] = useObjectState<any>({
    initialWorkflow: null,
    workflowInstance: null,
    workflowStage: null,
    checklistData: null,
    eocRecord: null,
    statisticsData: null,
  });
  const [modals, setModals] = useState({
    openDialog: false,
    disposition: false,
    reject: false,
    openDispositionExternal: false,
    assessment: false,
  });

  const handleTabChange = (e: React.SyntheticEvent, selectedTab: number) => {
    setCurrentTab(selectedTab);
  };

  const handle = async (data: any) => {
    try {
      await update(data);
      enqueueSnackbar(`${i18n('episodeOfCare.details.title', 'crs')} was updated.`);
    } catch (error) {
      enqueueSnackbar(`${i18n('episodeOfCare.details.title', 'crs')} was not updated.`, {
        variant: 'error',
      });
    } finally {
      await refresh();
    }
  };

  const toggleModal = useCallback((key: keyof typeof modals, value: boolean) => {
    setModals((prev) => ({ ...prev, [key]: value }));
  }, []);

  const dataMappingChecklistItem = useMemo(
    () => ({
      ...buildMappingChecklistItem('ObservationDetails', 'observations', observations, userEmail),
      ...buildMappingChecklistItem('Tasks', 'tasks', tasks, userEmail),
      ...buildMappingChecklistItem(
        'ServiceRequests',
        'serviceRequestPlanning',
        serviceRequests,
        userEmail
      ),
      ...buildMappingChecklistItem('Cases', 'casesMonitoring', caseData, userEmail),
    }),
    [observations, tasks, serviceRequests, caseData]
  );

  const assignCaseItemHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    await assignOwnerHandler({
      record: {},
      workflowInstance,
      userEmail,
      referenceValue: episodeOfCareId,
      referenceName: eocRecord?.workflowReferenceName,
      checklistItem,
      checklistData,
      more,
      onFinally: async () => {
        await fetchRecord(episodeOfCareId);
        await fetchWorkflowData(eocRecord?.workflowReferenceName);
      },
    });
  };

  const confirmHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    data,
  }: any) => {
    let workflowDataScope: any = null;
    let modifiedFields: any = null;

    if (data?.key && data?.resource) {
      workflowDataScope = buildMappingChecklistItem(
        checklistItem?.code,
        data.key,
        data.resource,
        userEmail
      );
      modifiedFields = { [data.key]: workflowDataScope };
    }

    const metadata = checklistItem?.metadata && JSON.parse(checklistItem.metadata);
    const { allowNonExistingInformation = null, message = null } = metadata?.ui?.data ?? {};

    const getData =
      dataMappingChecklistItem[metadata?.ui?.component?.typeComponent ?? 'default'] ?? null;

    if (!isEmpty(getData?.[getData.key])) {
      const { key } = getData;
      const data = getData[key];

      const dataScope = workflowInstanceData(
        workflowInstance,
        `checklistItem.${checklistItem.code}.modifiedFields.${key}`
      );

      if (Array.isArray(data)) {
        workflowDataScope = Array.isArray(dataScope) ? [...dataScope] : [];

        for (const newData of data) {
          const existingIndex = workflowDataScope?.findIndex(
            (item) => item?.fhirReference === newData?.fhirReference
          );

          if (existingIndex !== -1) {
            workflowDataScope[existingIndex] = newData;
          } else {
            workflowDataScope.push(newData);
          }
        }
      } else if (isObject(data)) {
        workflowDataScope = data;
      }

      modifiedFields = { [key]: workflowDataScope };
    }

    if (allowNonExistingInformation) {
      modifiedFields = { message: message };
    }

    if (isEmpty(workflowDataScope) && !allowNonExistingInformation) {
      await confirmDialog({
        key: 'this',
        render: () => <NonExistingInformation i18n={i18n} closeDialog={closeDialog} />,
      });
      return;
    }

    const payloadForUpdateWorkflowinstance = {
      referenceValue: episodeOfCareId,
      referenceName: eocRecord?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        eocRecord,
        payloadForUpdateWorkflowinstance,
        checklistItem ? checklistItem : checklistData,
        userEmail,
        modifiedFields
      );
    } catch (err) {
      console.log('error', err);
    } finally {
      await fetchRecord(episodeOfCareId);
      await fetchWorkflowData(eocRecord?.workflowReferenceName);
    }
  };

  const createWorkflowHandler = useCallback(
    (modalKey: keyof typeof modals) =>
      ({ checklistItem }: any) => {
        toggleModal(modalKey, true);
        updateWorkflowDetail({ checklistData: checklistItem });
      },
    [toggleModal]
  );

  const assessmentHandler = createWorkflowHandler('assessment');

  const fetchRecord = async (id: any) => {
    const result: any = await EpisodeOfCareService.getOne(id);
    updateWorkflowDetail({ eocRecord: result });
  };

  const fetchWorkflowData = async (workflowReferenceName: string | undefined) => {
    const result = await WorkflowService.getInstance(episodeOfCareId, workflowReferenceName);

    updateWorkflowDetail({
      workflowInstance: result,
      initialWorkflow: result?.workFlow?.code,
      workflowStage: result?.stage?.id,
    });
  };

  const fetchStatistics = async (episodeOfCareId: string | null) => {
    if (!episodeOfCareId) return;
    const result = await EpisodeOfCareService.getStatistics(episodeOfCareId);
    updateWorkflowDetail({ statisticsData: result });
  };

  useEffect(() => {
    if (!episodeOfCareId) {
      return;
    }
    fetchRecord(episodeOfCareId);
    fetchStatistics(episodeOfCareId);
  }, [episodeOfCareId]);

  useEffect(() => {
    if (!eocRecord) return;
    fetchWorkflowData(eocRecord?.workflowReferenceName);
  }, [eocRecord?.workflowReferenceName]);

  const eocWorkflowData: WorkflowData = useMemo(
    () => ({
      id: 1,
      recordId: episodeOfCareId,
      recordReference: eocRecord?.workflowReferenceName,
      stage: {
        id: workflowStage,
      },
      eocRecord,
      workflowInstance,
    }),
    [workflowStage, workflowInstance, eocRecord]
  );

  const userOrganizations = useMemo(() => authUser?.organizations ?? [], [authUser?.organizations]);

  const getAllUsers = useCallback(async () => {
    // Don't run if workflow instance is not loaded
    if (!workflowInstance?.scope) {
      return;
    }

    try {
      // Extract ACL requirements from workflow metadata
      const workflowMetadata = workflowInstance?.workFlow?.metadata
        ? JSON.parse(workflowInstance.workFlow.metadata)
        : {};
      const ownerACLs = workflowMetadata?.workflow?.ownerAllowedACLs;

      let result;
      if (ownerACLs && ownerACLs.length > 0) {
        // Use ACL-based filtering when configuration exists
        const relatedACLs = getRelatedAcls(ownerACLs);
        result = await UserService.getAllUsersByAcls(relatedACLs);
      } else {
        // Fallback to existing behavior when no ACL configuration
        result = await UserService.getUsersByOrgAndRole(userOrganizations, []);
      }

      setAllUsers(
        sortBy(
          result.filter((e: any) => e.status === 'Active'),
          ['firstName', 'LastName']
        )
      );
    } catch (err) {
      console.log('error on fetching all users', err);
    }
  }, [workflowInstance, userOrganizations]);

  // Call getAllUsers when workflow instance is loaded
  useEffect(() => {
    if (workflowInstance?.scope) {
      getAllUsers();
    }
  }, [workflowInstance?.scope]);

  const BaseHandlers = {
    assignOwner: {
      type: 'payload',
      handler: assignCaseItemHandler,
      data: allUsers,
    },
    confirm: {
      type: 'confirm',
      handler: confirmHandler,
      data: false,
    },
    assessment: {
      type: 'payload',
      handler: assessmentHandler,
      data: true,
    },

    // Workflow transition actions
    setDisposition: {
      type: 'confirm',
      handler: (open: boolean = true) => toggleModal('disposition', open),
      data: true,
    },
    setReject: {
      type: 'confirm',
      handler: (open: boolean = true) => toggleModal('reject', open),
      data: false,
    },
    setOpenDialog: {
      type: 'openDialog',
      handler: (open: boolean = true) => toggleModal('openDialog', open),
      data: false,
    },
  };

  const WorkflowHandler = {
    ...BaseHandlers,
    ASSIGN_EPISODEOFCARE_ITEM: BaseHandlers.assignOwner,
    ASSESSMENT_EPISODEOFCARE_ITEM: BaseHandlers.assessment,
    CONFIRM: BaseHandlers.confirm,
  };

  const WorkflowData = {
    disposition: modals.disposition,
    reject: modals.reject,
    openDialog: modals.openDialog,
    openDispositionExternal: modals.openDispositionExternal,
  };

  const taskMap = {
    tasks: tasks,
  };

  registerSteps({
    ObservationDetails: () => (
      <Card>
        <ObservationDetails
          patient={subject}
          customQuery={{ patient: subject?.id }}
          showEmpty={!subject?.id}
          permissions={{
            isAllowedAdd: checkAclValidation({
              acls: [crsAcls.CRS.EPISODE_OF_CARE.OBSERVATION.ADD],
            }),
            isAllowedEdit: checkAclValidation({
              acls: [crsAcls.CRS.EPISODE_OF_CARE.OBSERVATION.EDIT],
            }),
          }}
          handlerAction={updateObservation}
        />
      </Card>
    ),
    Tasks: (props: any) => {
      const propOther = props?.props;
      const ownerFilterPR = practitionerRoles.filter(Boolean);

      const { owner, ...rest } = propOther?.filters ?? {};

      const filters = {
        ...rest,
        ...(owner && ownerFilterPR.length ? { owner: ownerFilterPR } : {}),
      };

      const {
        isPatient = true,
        isCarePlan = true,
        taskSource = 'filtered',
        activityDefinition = 'case',
        showAssessmentChecklistDataButton = false,
      } = propOther?.componentProps ?? {};

      const resolvedTasks = taskMap[taskSource] ?? [];

      return (
        <TasksByPathway
          permissions={{
            ...taskPermissions,
            ...(showAssessmentChecklistDataButton && { isAllowedToAdd: false }),
          }}
          taskActivityDefinitionContext={
            activityDefinition ? taskActivityDefinition?.[activityDefinition] : undefined
          }
          tasks={resolvedTasks}
          patient={isPatient ? (subject as WrappedPatient) : null}
          healthCareServices={[]}
          serviceRequests={[]}
          menuTaskHandlers={commonMenuTaskHandlers}
          onAssessmentSelect={handleAssessmentSelect}
          filterTasksFromChecklistItem={Object.keys(filters).length > 0 ? filters : undefined}
          isWorkflowContext={true}
          childrenButtons={
            showAssessmentChecklistDataButton ? (
              <AssessmentChecklistDataButton followUpHandler={assessmentHandler} i18n={i18n} />
            ) : undefined
          }
        />
      );
    },
    ServiceRequests: () => (
      <PatientGenericServiceRequest
        patient={subject}
        permissions={serviceRequestPermissions}
        observations={observations}
        conditions={conditions}
      />
    ),
    Cases: () => <PatientCase patientId={subject?.id} i18n={i18n} />,
  });

  const taskPermissions: TaskPermissions = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.EPISODE_OF_CARE.TASKS.ADD] }),
      isAllowedToEdit: checkAclValidation({ acls: [crsAcls.CRS.EPISODE_OF_CARE.TASKS.EDIT] }),
      notes: {
        isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.EPISODE_OF_CARE.TASKS.NOTES.ADD] }),
        isAllowedToView: checkAclValidation({
          acls: [crsAcls.CRS.EPISODE_OF_CARE.TASKS.NOTES.VIEW],
        }),
      },
      isAllowedToAssignProgram: checkAclValidation({
        acls: [crsAcls.CRS.EPISODE_OF_CARE.TASKS.HANDLER.ASSIGN_PROGRAM],
      }),
      isAllowedReferralInternal: checkAclValidation({
        acls: [crsAcls.CRS.EPISODE_OF_CARE.TASKS.HANDLER.REFERRAL_INTERNAL],
      }),
      isAllowedReferralExternal: checkAclValidation({
        acls: [crsAcls.CRS.EPISODE_OF_CARE.TASKS.HANDLER.REFERRAL_EXTERNAL],
      }),
      isAllowedAppointmentScheduling: checkAclValidation({
        acls: [crsAcls.CRS.EPISODE_OF_CARE.TASKS.HANDLER.APPOINTMENT],
      }),
      isAllowedWarmHandoff: checkAclValidation({
        acls: [crsAcls.CRS.EPISODE_OF_CARE.TASKS.HANDLER.WARM_HANDOFF],
      }),
    }),
    []
  );

  const serviceRequestPermissions: ServiceRequestsPermissions = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation({
        acls: [crsAcls.CRS.EPISODE_OF_CARE.SERVICE_REQUESTS.ADD],
      }),
      isAllowedToEdit: checkAclValidation({
        acls: [crsAcls.CRS.EPISODE_OF_CARE.SERVICE_REQUESTS.EDIT],
      }),
      isAllowedToViewNotes: checkAclValidation({
        acls: [crsAcls.CRS.EPISODE_OF_CARE.SERVICE_REQUESTS.NOTES.VIEW],
      }),
      taskPermissions: {
        isAllowedToAdd: checkAclValidation({
          acls: [crsAcls.CRS.EPISODE_OF_CARE.SERVICE_REQUESTS.TASKS.ADD],
        }),
        isAllowedToEdit: checkAclValidation({
          acls: [crsAcls.CRS.EPISODE_OF_CARE.SERVICE_REQUESTS.TASKS.EDIT],
        }),
        isAllowedToAssignProgram: checkAclValidation({
          acls: [crsAcls.CRS.EPISODE_OF_CARE.SERVICE_REQUESTS.TASKS.HANDLER.ASSIGN_PROGRAM],
        }),
        isAllowedReferralInternal: checkAclValidation({
          acls: [crsAcls.CRS.EPISODE_OF_CARE.SERVICE_REQUESTS.TASKS.HANDLER.REFERRAL_INTERNAL],
        }),
        isAllowedReferralExternal: checkAclValidation({
          acls: [crsAcls.CRS.EPISODE_OF_CARE.SERVICE_REQUESTS.TASKS.HANDLER.REFERRAL_EXTERNAL],
        }),
        isAllowedAppointmentScheduling: checkAclValidation({
          acls: [crsAcls.CRS.EPISODE_OF_CARE.SERVICE_REQUESTS.TASKS.HANDLER.APPOINTMENT],
        }),
        isAllowedWarmHandoff: checkAclValidation({
          acls: [crsAcls.CRS.EPISODE_OF_CARE.SERVICE_REQUESTS.TASKS.HANDLER.WARM_HANDOFF],
        }),
        notes: {
          isAllowedToView: checkAclValidation({
            acls: [crsAcls.CRS.EPISODE_OF_CARE.SERVICE_REQUESTS.TASKS.NOTES.VIEW],
          }),
          isAllowedToAdd: checkAclValidation({
            acls: [crsAcls.CRS.EPISODE_OF_CARE.SERVICE_REQUESTS.TASKS.NOTES.ADD],
          }),
        },
      },
    }),
    []
  );

  const memoizedWorkflowInstanceRefresh = useCallback(
    (data?: any) => {
      fetchStatistics(episodeOfCareId);
      if (data && data?.isWorkflowContext) fetchWorkflowData(eocRecord?.referen);
    },
    [episodeOfCareId, fetchWorkflowData]
  );

  const memoizedRefreshTasksSimple = useCallback(
    (data?: any) => {
      refreshTasks();
      memoizedWorkflowInstanceRefresh(data);
    },
    [memoizedWorkflowInstanceRefresh, refreshTasks]
  );

  const memoizedRefreshTasks = useCallback((data?: any) => {
    refreshTasks();
    memoizedWorkflowInstanceRefresh(data);
  }, []);

  useEffect(() => {
    onEvent(CREATE_TASK, memoizedRefreshTasks);
    onEvent(UPDATE_TASK, memoizedRefreshTasksSimple);
    return () => {
      removeEvent(CREATE_TASK, memoizedRefreshTasks);
      removeEvent(UPDATE_TASK, memoizedRefreshTasksSimple);
    };
  }, []);

  if (isLoading) return <LoadingScreen />;

  return (
    <PageContainer
      pageTitle={i18n('episodeOfCare.details.title', 'crs')}
      title={i18n('episodeOfCare.details.title', 'crs')}
      breadCrumbsLinks={[
        { name: `${i18n('admin.list.dashboard')}`, href: PATH_DASHBOARD.root },
        { name: `${i18n('breadcrumbs.title', 'crs')}` },
        {
          name: i18n('episodeOfCare.list.list', 'crs'),
          href: PATH_DASHBOARD.crs.episodeOfCare.list,
        },
        { name: i18n('episodeOfCare.details.title', 'crs') },
      ]}
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={8} sx={{ display: 'flex' }}>
          <WorkflowHeader
            patient={subject ?? null}
            configurations={configurations}
            i18n={i18n}
            sx={{
              mb: 0,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              boxShadow: 1,
            }}
          />
        </Grid>
        <Grid item xs={2} sx={{ display: 'flex' }}>
          <ItemBox>
            <Typography
              sx={{
                fontSize: '10px',
                textTransform: 'uppercase',
                mb: 0.5,
                color: 'text.secondary',
                fontWeight: 500,
                letterSpacing: '0.08em',
              }}
              variant="caption"
            >
              {i18n('episodeOfCare.details.general.currentPeriod', 'crs')}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor: (theme) => theme.palette.primary.main + '20',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CalendarToday color="primary" fontSize="small" />
              </Box>

              <Box>
                <Typography fontWeight="bold">
                  {resource?.getPeriodShorFormat?.()?.start}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontStyle: 'italic',
                  }}
                >
                  {resource?.isOngoning?.()}
                </Typography>
              </Box>
            </Box>
          </ItemBox>
        </Grid>
        <Grid item xs={2} sx={{ display: 'flex' }}>
          <ItemBox>
            <Typography
              sx={{
                fontSize: '10px',
                textTransform: 'uppercase',
                mb: 0.5,
                color: 'text.secondary',
                fontWeight: 500,
                letterSpacing: '0.08em',
              }}
              variant="caption"
            >
              {i18n('episodeOfCare.details.general.type', 'crs')}
            </Typography>

            <Typography
              sx={{
                fontSize: '20px',
                color: '#00AB55',
                fontWeight: 600,
              }}
              variant="caption"
            >
              {resource?.getFirstType?.()?.code}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {resource?.getFirstType?.()?.display}
            </Typography>
          </ItemBox>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <WorkflowStatistics
          workflowTitle={workflowInstance?.workFlow?.name ?? ''}
          statisticsData={statisticsData}
          path={'case.details'}
          namespace={'crs'}
          i18n={i18n}
        />

        <Workflow
          data={eocWorkflowData}
          refreshRecord={() => {
            fetchWorkflowData(eocRecord?.workflowReferenceName);
            fetchStatistics(episodeOfCareId);
          }}
          initialWorkflow={initialWorkflow}
          workflowHandler={WorkflowHandler}
          workflowData={WorkflowData}
          i18Path="episodeOfCare.details.workflow"
          namespace="crs"
        />
      </Grid>

      <Tabs value={currentTab} onChange={handleTabChange} sx={{ marginTop: 2.5 }}>
        <Tab label={i18n('episodeOfCare.details.tabs.general', 'crs')} />
        <Tab label={i18n('episodeOfCare.details.tabs.statusHistory', 'crs')} />
        <Tab label={i18n('episodeOfCare.details.tabs.diagnosis', 'crs')} />
        <Tab label={i18n('episodeOfCare.details.tabs.documents', 'crs')} />
        <Tab label={i18n('episodeOfCare.details.tabs.case', 'crs')} />
      </Tabs>

      <Divider />

      <TabPanel value={currentTab} index={0}>
        <General eoc={eocMapped} handle={handle} />
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <StatusHistory eoc={resource} i18n={i18n} handle={handle} />
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        <Diagnosis eoc={resource} patient={subject} handle={handle} />
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        <Documents
          episodeOfCare={resource?.id}
          patient={subject}
          i18n={i18n}
          configurations={componentsData?.episodeOfCare}
        />
      </TabPanel>

      <TabPanel value={currentTab} index={4}>
        <Cases patient={subject} encounter={encounters?.[0]?.id} i18n={i18n} />
      </TabPanel>
    </PageContainer>
  );
}

export function ItemBox({ children, sx }: { children: ReactNode; sx?: any }) {
  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        p: 2,
        borderRadius: 1,
        boxShadow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
